import { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import type { LoginResult } from '@mealdesk/shared';
import AuthShell, { fieldClass } from '../components/AuthShell.js';
import { Button } from '../components/ui.js';
import { useT } from '../lib/i18n.js';
import { apiFetch } from '../lib/api.js';
import { useAuthStore } from '../stores/auth.js';

type Session = Omit<LoginResult, 'refreshToken'>;

const MIN = 10;

/**
 * Chooses the password the mailed link was issued for.
 *
 * Redeeming the link is the email verification: whoever opened it holds the
 * mailbox. The server signs them in at the same moment, so nobody types a
 * password they just invented twice in a row, and it drops every other
 * session — a reset that leaves the old one alive has reset nothing.
 */
export default function SetPasswordPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const setSession = useAuthStore((s) => s.setSession);
  const { t } = useT();

  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const token = params.get('token');

  if (!token) {
    return (
      <AuthShell
        title="This link is incomplete"
        subtitle="It is missing its token. Ask for a new one and open it from the email itself."
        footer={
          <Link to="/password" className="font-extrabold text-brand-ink">
            Send a new link
          </Link>
        }
      >
        <span />
      </AuthShell>
    );
  }

  const submit = async () => {
    if (password !== confirm) {
      setError('Those two do not match');
      return;
    }

    setBusy(true);
    setError(null);
    try {
      const session = await apiFetch<Session>('/api/auth/password/set', {
        method: 'POST',
        body: { token, password },
        anonymous: true,
      });
      setSession(session);
      navigate('/', { replace: true });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not set your password');
    } finally {
      setBusy(false);
    }
  };

  return (
    <AuthShell
      title={t('Choose a password')}
      subtitle={`At least ${MIN} characters. You will use it with your work email from now on.`}
      footer={
        <Link to="/signin" className="font-extrabold text-brand-ink">
          {t('Back to sign in')}
        </Link>
      }
    >
      <form
        className="flex flex-col"
        onSubmit={(e) => {
          e.preventDefault();
          void submit();
        }}
      >
        <label
          htmlFor="password"
          className="mb-2 block text-[13px] font-extrabold text-muted md:text-[14.5px]"
        >
          {t('New password')}
        </label>
        <input
          id="password"
          type="password"
          autoComplete="new-password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          className={fieldClass}
        />

        <label
          htmlFor="confirm"
          className="mb-2 block text-[13px] font-extrabold text-muted md:text-[14.5px]"
        >
          {t('Again')}
        </label>
        <input
          id="confirm"
          type="password"
          autoComplete="new-password"
          value={confirm}
          onChange={(e) => setConfirm(e.target.value)}
          className={fieldClass}
        />

        <Button type="submit" disabled={busy || password.length < MIN || confirm.length === 0}>
          {busy ? `${t('Save and sign in')}…` : t('Save and sign in')}
        </Button>

        {error && (
          <p className="mt-3.5 rounded-tile bg-danger-soft px-4 py-3.5 text-[13.5px] font-bold text-danger md:text-[15px]">
            {error}
          </p>
        )}

        <p className="mt-4 rounded-tile bg-white px-4 py-3.5 text-[13.5px] leading-[1.5] text-muted md:text-[15px]">
          Setting a password signs you out everywhere else. If you asked for this because somebody
          else had your account, that is the part that matters.
        </p>
      </form>
    </AuthShell>
  );
}
